import {
  Box,
  Flex,
  Text,
  Icon,
  Button,
  Image,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
} from '@chakra-ui/react'
import { useCallback, useState } from 'react'
import { useDropzone } from 'react-dropzone'
import { UploadCloud02Icon, XCloseIcon } from '../../assets/icons'
import VideoPlayer from '../cards/VideoPlayer'
import LazyLoadImg from '../image/LazyLoadImg'

interface MediaCardProps {
  media: string | null | File
  onMediaChange?: (file: File | null) => void
  isReadOnly?: boolean
  quizMode?: boolean
  isEditing?: boolean
}

const MediaCard: React.FC<MediaCardProps> = ({
  media,
  onMediaChange,
  isReadOnly = false,
  quizMode = false,
  isEditing = true,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const [currentFile, setCurrentFile] = useState<File | string | null>(media)

  const isVideo = (media: string | File) =>
    typeof media === 'string' ? media.includes('.mp4') : media.type === 'video/mp4'

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      const file = acceptedFiles[0]
      if (file) {
        const validTypes = ['image/png', 'image/gif', 'image/jpeg', 'video/mp4']
        if (!validTypes.includes(file.type)) {
          return
        }
        setCurrentFile(file)
        if (onMediaChange) {
          onMediaChange(file)
        }
      }
    },
    [onMediaChange],
  )

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'image/png': ['.png'],
      'image/gif': ['.gif'],
      'image/jpeg': ['.jpg', '.jpeg'],
      'video/mp4': ['.mp4'],
    },
    maxSize: 10 * 1024 * 1024 * 1024,
    disabled: isReadOnly || quizMode || !isEditing,
  })

  const handleDiscard = () => {
    if (onMediaChange) {
      onMediaChange(null)
    }
    setCurrentFile(null)
  }

  const renderMedia = (url: string | null | File, inModal: boolean = false) => {
    if (!url) return null

    const mediaUrl = typeof url === 'string' ? url : URL.createObjectURL(url)

    if (isVideo(url) && !inModal) {
      return (
        <Box
          position="relative"
          width="100%"
          maxH="300px"
          display="flex"
          bg="gray.800"
          alignItems="center"
          justifyContent="center"
        >
          <video width="100%" height="100%" preload="metadata">
            <source src={mediaUrl + '#t=1'} type="video/mp4" />
          </video>
          <Icon
            as="svg"
            viewBox="0 0 24 24"
            boxSize="60px"
            color="white"
            position="absolute"
            filter="drop-shadow(0px 4px 6px rgba(0, 0, 0, 0.5))"
          >
            <path fill="currentColor" d="M8 5v14l11-7z" />
          </Icon>
        </Box>
      )
    } else if (isVideo(url) && inModal) {
      return <VideoPlayer url={mediaUrl} onRemove={() => {}} startPlay={false} />
    }

    return (
      <LazyLoadImg
        src={mediaUrl}
        alt="Media content"
        w="100%"
        h={inModal ? 'auto' : '180px'}
        objectFit="cover"
        borderRadius="md"
        _hover={{ opacity: 0.9 }}
        transition="opacity 0.2s"
        transform={inModal ? `scale(1)` : undefined}
        draggable="false"
      />
    )
  }

  const shouldShowReadOnlyView = isReadOnly || quizMode || !isEditing

  if (shouldShowReadOnlyView) {
    return (
      <Box w="100%" borderRadius="lg" overflow="hidden" maxH="200px" borderWidth="1px">
        <Box cursor="pointer" onClick={onOpen} position="relative" borderRadius="md" overflow="hidden">
          {renderMedia(currentFile)}
        </Box>

        <Modal isOpen={isOpen} onClose={onClose} isCentered size="xl">
          <ModalOverlay />
          <ModalContent bg="transparent" maxW="90vw" maxH="90vh">
            <ModalBody p={0} bg="transparent" position="relative">
              <Box maxW="100%" maxH="90vh" display="flex" justifyContent="center" alignItems="center">
                {renderMedia(currentFile, true)}
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>
      </Box>
    )
  }

  return (
    <Box w="100%" borderWidth="1px" borderRadius="lg" maxH="200px" borderColor="solid.50">
      {currentFile ? (
        <Box
          position="relative"
          borderRadius="md"
          maxH="180px"
          borderWidth="1px"
          borderColor="solid.50"
          overflow="hidden"
        >
          {renderMedia(currentFile)}

          <Button variant={'imageButton'} top="8px" right="8px" position="absolute" onClick={handleDiscard}>
            <Icon boxSize="20px" color="solid.25">
              <XCloseIcon />
            </Icon>
          </Button>
        </Box>
      ) : (
        <Box
          {...getRootProps()}
          p="4"
          h="150px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          cursor={isReadOnly || !isEditing ? 'default' : 'pointer'}
          bg={isDragActive ? 'primary.25' : 'white'}
          borderColor={isDragActive ? 'primary.100' : 'solid.50'}
        >
          <input {...getInputProps()} />
          <Flex direction="column" align="center">
            <Icon boxSize="36px" color="solid.500">
              <UploadCloud02Icon />
            </Icon>
            <Text textStyle="h-sm" color="solid.900">
              Drop a file here
            </Text>
            <Text textStyle="l-xs" mt="0" color="solid.400">
              or{' '}
              <Text as="span" textDecoration="underline" color="solid.900">
                Browse Files
              </Text>{' '}
              to add new content
            </Text>
            <Text textStyle="l-xs" mt="0" color="solid.400">
              Supported formats: PNG, GIF, JPEG, MP4 (Max. 10GB)
            </Text>
          </Flex>
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="sm" closeOnOverlayClick>
        <ModalOverlay />
        <ModalContent>
          <ModalBody p={0}>{renderMedia(currentFile, true)}</ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  )
}

export default MediaCard
